/**
 * submissionService.ts
 * Handles student answer submissions to Firebase RTDB.
 *
 * Submissions live in RTDB for real-time latency:
 *   /rooms/{roomCode}/submissions/{studentId}
 *
 * Scoring and XP award are computed here after submission completes.
 */

import {
  ref,
  set,
  get,
  update,
  increment,
  onValue,
  off,
  type Unsubscribe,
  type DatabaseReference,
} from 'firebase/database';
import {
  ref as storageRef,
  uploadString,
  getDownloadURL,
} from 'firebase/storage';
import { getRtdb, getStorageInstance } from './firebase';
import { awardQuizXP, findStudentIdByName } from './studentService';
import type { StudentAnswer, Submission, MCResults, WordCloudResults } from '@types-local/room.types';
import type { MCConfig } from '@types-local/quiz.types';

// ---- Path helpers ------------------------------------------

const submissionsRef = (roomCode: string): DatabaseReference =>
  ref(getRtdb(), `rooms/${roomCode}/submissions`);

const submissionRef = (roomCode: string, studentId: string): DatabaseReference =>
  ref(getRtdb(), `rooms/${roomCode}/submissions/${studentId}`);

// ---- Submit ------------------------------------------------

/**
 * Submits a student answer to RTDB.
 * For drawing answers, uploads the base64 image to Firebase Storage first.
 */
export async function submitAnswer(
  roomCode: string,
  studentId: string,
  studentName: string,
  slideId: string,
  answer: StudentAnswer,
  _classId: string,
): Promise<void> {
  let processedAnswer = answer;

  // Handle drawing: upload base64 to Storage and swap for public URL
  if (answer.type === 'slide_drawing' && answer.imageDataUrl.startsWith('data:')) {
    const path = `submissions/${roomCode}/${studentId}_drawing.png`;
    const sRef = storageRef(getStorageInstance(), path);
    await uploadString(sRef, answer.imageDataUrl, 'data_url');
    const fileUrl = await getDownloadURL(sRef);
    processedAnswer = { ...answer, imageDataUrl: fileUrl };
  }

  // Handle media upload: the student captures an image as a data URL; upload
  // it to Storage and swap fileUrl for the public download URL.
  if (answer.type === 'media_upload' && answer.fileUrl.startsWith('data:')) {
    const ext = answer.fileName.split('.').pop()?.toLowerCase() || 'jpg';
    const path = `submissions/${roomCode}/${studentId}_media.${ext}`;
    const sRef = storageRef(getStorageInstance(), path);
    await uploadString(sRef, answer.fileUrl, 'data_url', { contentType: answer.mimeType });
    const fileUrl = await getDownloadURL(sRef);
    processedAnswer = { ...answer, fileUrl };
  }

  const submission: Submission = {
    studentId,
    studentName,
    slideId,
    answer: processedAnswer,
    score: 0, // scored separately via scoreSubmission()
    submittedAt: Date.now(),
  };

  await set(submissionRef(roomCode, studentId), submission);
}

/**
 * Scores an MC submission against the correct answer config.
 * Awards XP if correct. Returns the score (0 or 1).
 */
export async function scoreSubmission(
  roomCode: string,
  studentId: string,
  classId: string,
  config: MCConfig,
  firstRespondents: string[], // student IDs in submission order for speed bonus
): Promise<number> {
  const snap = await new Promise<Submission | null>((resolve) => {
    const r = submissionRef(roomCode, studentId);
    onValue(r, (s) => {
      off(r, 'value');
      resolve(s.exists() ? (s.val() as Submission) : null);
    }, { onlyOnce: true });
  });

  if (!snap || snap.answer.type !== 'multiple_choice') return 0;

  const isCorrect = snap.answer.selectedIndex === config.options.findIndex((o) => o.isCorrect);
  const isSpeedBonus =
    config.competitionMode &&
    firstRespondents.indexOf(studentId) < 3; // top 3 fastest get bonus

  const score = isCorrect ? (isSpeedBonus ? 2 : 1) : 0;
  await set(ref(getRtdb(), `rooms/${roomCode}/submissions/${studentId}/score`), score);

  // Session scoreboard: every student who joins and plays counts, no
  // pre-built roster required. Accumulate this question's points.
  await update(ref(getRtdb(), `rooms/${roomCode}/scoreboard/${studentId}`), {
    name: snap.studentName,
    score: increment(score),
    correct: increment(isCorrect ? 1 : 0),
  });

  // Best-effort: if a matching roster student exists, also award
  // persistent XP/stars. Silently skipped when there's no match.
  const rosterId = await findStudentIdByName(classId, snap.studentName);
  if (rosterId) {
    await awardQuizXP(classId, rosterId, isCorrect, isSpeedBonus);
  }

  return score;
}

/**
 * Uploads a captured slide image to Storage and points the room at it,
 * so students can view the current slide (used for slides without an
 * activity, and as a backdrop in general).
 */
export async function pushSlideImage(
  roomCode: string,
  slideIndex: number,
  base64Png: string,
): Promise<void> {
  const path = `rooms/${roomCode}/slides/slide_${slideIndex}.png`;
  const sRef = storageRef(getStorageInstance(), path);
  await uploadString(sRef, base64Png, 'base64', { contentType: 'image/png' });
  const url = await getDownloadURL(sRef);
  await update(ref(getRtdb(), `rooms/${roomCode}`), { slideImageUrl: url });
}

// ---- Session scoreboard ------------------------------------

export interface ScoreboardEntry {
  name: string;
  score: number;
  correct: number;
}

/**
 * Reads the session scoreboard (cumulative points per student across
 * all scored questions in the room), sorted highest first.
 */
export async function getSessionLeaderboard(roomCode: string): Promise<ScoreboardEntry[]> {
  const snap = await get(ref(getRtdb(), `rooms/${roomCode}/scoreboard`));
  if (!snap.exists()) return [];
  const raw = snap.val() as Record<string, Partial<ScoreboardEntry>>;
  return Object.values(raw)
    .map((e) => ({ name: e.name ?? 'Student', score: e.score ?? 0, correct: e.correct ?? 0 }))
    .sort((a, b) => b.score - a.score);
}

/**
 * Subscribes to the raw submission list for the active room.
 * Used by non-MC activities (word cloud, short answer, drawing) that
 * display responses rather than an aggregated tally.
 */
export function subscribeSubmissions(
  roomCode: string,
  callback: (submissions: Submission[]) => void,
): Unsubscribe {
  const r = submissionsRef(roomCode);
  const listener = onValue(r, (snapshot) => {
    const submissions: Submission[] = [];
    snapshot.forEach((child) => { submissions.push(child.val() as Submission); });
    callback(submissions);
  });
  return () => off(r, 'value', listener);
}

/**
 * Awards participation points (1 each) to every student who responded to
 * the current open-ended activity, accumulating into the session
 * scoreboard so they appear on the leaderboard alongside MC scorers.
 */
export async function awardParticipation(roomCode: string): Promise<void> {
  const snap = await get(submissionsRef(roomCode));
  if (!snap.exists()) return;
  const subs = snap.val() as Record<string, Submission>;
  for (const [studentId, sub] of Object.entries(subs)) {
    await update(ref(getRtdb(), `rooms/${roomCode}/scoreboard/${studentId}`), {
      name: sub.studentName,
      score: increment(1),
    });
  }
}

// ---- Real-time results subscriptions -----------------------

/**
 * Subscribes to all submissions for the active room and aggregates
 * MC results (vote counts per option).
 */
export function subscribeMCResults(
  roomCode: string,
  optionCount: number,
  callback: (results: MCResults) => void,
): Unsubscribe {
  const r = submissionsRef(roomCode);
  const listener = onValue(r, (snapshot) => {
    const counts = new Array<number>(optionCount).fill(0);
    const submissions: Submission[] = [];

    snapshot.forEach((child) => {
      const sub = child.val() as Submission;
      if (sub.answer.type === 'multiple_choice') {
        const idx = sub.answer.selectedIndex;
        if (idx >= 0 && idx < optionCount) counts[idx]++;
        submissions.push(sub);
      }
    });

    callback({ counts, totalResponses: submissions.length, submissions });
  });

  return () => off(r, 'value', listener);
}

/**
 * Subscribes to all submissions and aggregates Word Cloud word frequencies.
 */
export function subscribeWordCloudResults(
  roomCode: string,
  callback: (results: WordCloudResults) => void,
): Unsubscribe {
  const r = submissionsRef(roomCode);
  const listener = onValue(r, (snapshot) => {
    const wordFrequency: Record<string, number> = {};
    let totalResponses = 0;

    snapshot.forEach((child) => {
      const sub = child.val() as Submission;
      if (sub.answer.type === 'word_cloud') {
        totalResponses++;
        for (const word of sub.answer.words) {
          const normalized = word.toLowerCase().trim();
          if (normalized) {
            wordFrequency[normalized] = (wordFrequency[normalized] ?? 0) + 1;
          }
        }
      }
    });

    callback({ wordFrequency, totalResponses });
  });

  return () => off(r, 'value', listener);
}
